import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import React, { useState } from "react";
import { Col, Container, Row, Spinner } from "react-bootstrap";
import { TbFileExport } from "react-icons/tb";
import CsvDownload from "react-json-to-csv";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";

import { GoogleMapsDashboard } from "../../../../../../components/GoogleMapsDashboard";
import { Loading } from "../../../../../../components/Loading";
import { useDidMountEffect } from "../../../../../../hooks/useDidMountEffect";
import { BrComerceApiService } from "../../../../../../services/BrComerceApiService";
import * as authDuck from "../../../../../../store/ducks/auth.duck";
import store from "../../../../../../store/store";
import { ChartBySla } from "../../partials/ChartBySla";
import { ClientFilters } from "../../partials/Filters/ClientFilters";
import { TableCustomerShipping } from "../../partials/TableCustomerShipping";
import { TableStatusShippings } from "../../partials/TableStatusShippings";
import styles from "./dashboardClient.module.scss";
import { StyledTooltip } from "../../../../components/Tooltip";
import { findShippingLabel } from "../../utils/ShippingStatus";
import { DeliveryTypeTitleEnum } from "../../../../../../enums/DeliveryTypeEnum";
import { resultsPerPage } from "../../utils/limiters";

const tableTypes = {
  tracking: "tracking",
  reverse: "reverse"
}

const DashboardClient = () => {
  const api = new BrComerceApiService();

  const { auth } = store.getState();
  const { user } = auth;

  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFilters] = useState(null);

  const [statusTable, setStatusTable] = useState([]);
  const [chartBySLA, setChartBySLA] = useState(null);
  const [chartLocations, setChartLocations] = useState([]);

  const [tableTrackingIndicator, setTableTrackingIndicator] = useState([]);
  const [tableTrackingExport, setTableTrackingExport] = useState([]);
  const [pageTracking, setPageTracking] = useState(1);
  const [hasMoreTracking, setHasMoreTracking] = useState(true);
  const [isTrackingTableLoading, setIsTrackingTableLoading] = useState(false);

  const [tableReverseLogisticIndicator, setTableReverseLogisticIndicator] = useState([]);
  const [tableReverseExport, setTableReverseExport] = useState([]);
  const [pageReverse, setPageReverse] = useState(1);
  const [hasMoreReverse, setHasMoreReverse] = useState(true);
  const [isReverseTableLoading, setIsReverseTableLoading] = useState(false);

  const [userWalletBalance, setUserWalletBalance] = useState(0);
  const [userBalance, setUserBalance] = useState(0);

  const handleLoadTableStatus = response => {
    setStatusTable(response);
  };

  const handleLoadCharts = ({
    chartBySLA,
    chartGeolocation,
  }) => {
    setChartBySLA(chartBySLA);

    const handledGeolocations = [];
    if (chartGeolocation.length) {
      chartGeolocation.forEach(item => {
        handledGeolocations.push({
          lat: Number(item?.latitude),
          lng: Number(item?.longitude),
          amount: Number(item?.amount)
        });
      });
    }

    setChartLocations(handledGeolocations);
  };

  const fetchTableShippings = async (filters, table) => {
    const allFilters = {
      ...filters,
      resultsPerPage
    }

    const loadCustomerTables = await api.makeHttpRequest({
      method: "GET",
      url: `/dashboard/customerTables/${table}`,
      params: allFilters
    });

    if (!loadCustomerTables) {
      throw new Error("Erro ao carregar as tabelas");
    }

    if (table === tableTypes.tracking) {
      setPageTracking((prevPage) => prevPage + 1);
    } else {
      setPageReverse((prevPage) => prevPage + 1);
    }

    return loadCustomerTables
  }

  const handleLoadedTables = (items, table) => {
    if (table === tableTypes.tracking) {
      setTableTrackingIndicator(prev => [...prev, ...items]);

      const exportData = items.map(tracking => ({
        SRO: tracking?.sro,
        Pedido: tracking?.orderNumber ?? tracking?.name,
        Coleta: tracking?.idCollect,
        Tipo: DeliveryTypeTitleEnum[tracking?.type],
        'Status do objeto': findShippingLabel(tracking?.idShippingStatus),
        'Criado em': tracking?.created_at ? format(new Date(tracking?.created_at), "dd/MM/yyyy - HH:mm") : "",
        'Previsão de entrega': tracking?.previewDate ? format(new Date(tracking?.previewDate), "dd/MM/yyyy") : "",
        'Data de entrega': tracking?.deliveredDate ? format(new Date(tracking?.deliveredDate), "dd/MM/yyyy - HH:mm") : "",
        'Destinatário': tracking?.destination?.name,
        Origem: tracking?.origin?.name,
      }))
      setTableTrackingExport(prev => [...prev, ...exportData])

      if (items.length < resultsPerPage) {
        setHasMoreTracking(false);
      }
    } else {
      setTableReverseLogisticIndicator(prev => [...prev, ...items]);

      const exportData = items.map(tracking => ({
        'Código de autorização': tracking?.authorizationCode,
        Pedido: tracking?.orderNumber ?? tracking?.name,
        Coleta: tracking?.idCollect,
        Tipo: DeliveryTypeTitleEnum[tracking?.type],
        'Status do objeto': findShippingLabel(tracking?.idShippingStatus),
        'Criado em': tracking?.created_at ? format(new Date(tracking?.created_at), "dd/MM/yyyy - HH:mm") : "",
        'Previsão de entrega': tracking?.previewDate ? format(new Date(tracking?.previewDate), "dd/MM/yyyy") : "",
        'Data de entrega': tracking?.deliveredDate ? format(new Date(tracking?.deliveredDate), "dd/MM/yyyy - HH:mm") : "",
        'Destinatário': tracking?.destination?.name,
        Origem: tracking?.origin?.name,
      }))
      setTableReverseExport(prev => [...prev, ...exportData])

      if (items.length < resultsPerPage) {
        setHasMoreReverse(false);
      }
    }
  };

  const loadMoreTrackingItems = async () => {
    try {
      setIsTrackingTableLoading(true);

      const result = await fetchTableShippings({ ...filters, page: pageTracking }, tableTypes.tracking);
      handleLoadedTables(result ?? [], tableTypes.tracking);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Tabelas",
        text: `Ocorreu um erro ao carregar as tabelas`
      });
    } finally {
      setIsTrackingTableLoading(false);
    }

  };

  const loadMoreReverseItems = async () => {
    try {
      setIsReverseTableLoading(true);

      const result = await fetchTableShippings({ ...filters, page: pageReverse }, tableTypes.reverse);
      handleLoadedTables(result ?? [], tableTypes.reverse);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Tabelas",
        text: `Ocorreu um erro ao carregar as tabelas`
      });
    } finally {
      setIsReverseTableLoading(false);
    }
  };

  const loadCharts = async allFilters => {
    const listErrors = [];

    const loadTableStatusPromise = api.makeHttpRequest({
      method: "PUT",
      url: "/dashboard/shippingStatus/",
      data: allFilters
    });

    const loadCustomerIndicatorsPromise = api.makeHttpRequest({
      method: "PUT",
      url: "/dashboard/customerIndicators/",
      data: allFilters
    });

    const [
      loadedTableStatus,
      loadCustomerIndicators
    ] = await Promise.allSettled([
      loadTableStatusPromise,
      loadCustomerIndicatorsPromise
    ]);

    if (loadCustomerIndicators?.status === "fulfilled") {
      handleLoadCharts(loadCustomerIndicators?.value);
    } else {
      listErrors.push("Erro ao carregar indicadores");
    }

    if (loadedTableStatus?.status === "fulfilled") {
      handleLoadTableStatus(loadedTableStatus?.value);
    } else {
      listErrors.push("Erro ao carregar tabela com status das entregas");
    }

    if (!!listErrors?.length) {
      throw listErrors;
    }
  };

  const loadUserBalance = async () => {
    try {
      const result = await api.makeHttpRequest({
        method: "GET",
        url: "/users/me/"
      });

      store.dispatch(authDuck.actions.fulfillUser(result));

      if (result.walletBalance) {
        setUserWalletBalance(Number(result.walletBalance));
      }
      if (result.balance) {
        setUserBalance(Number(result.balance));
      }
    } catch (e) {
      throw new Error(["Ocorreu um erro ao carregar usuários"]);
    }
  };

  useDidMountEffect(() => {
    const resetData = async () => {
      try {
        setIsLoading(true);
        setIsTrackingTableLoading(true);
        setIsReverseTableLoading(true);
        setTableTrackingIndicator([]);
        setTableTrackingExport([]);
        setTableReverseLogisticIndicator([]);
        setTableReverseExport([]);
        setPageTracking(1);
        setPageReverse(1);
        setHasMoreReverse(true);
        setHasMoreTracking(true);

        const [tracking, reverse] = await Promise.all([
          fetchTableShippings({ ...filters, page: 1 }, tableTypes.tracking),
          fetchTableShippings({ ...filters, page: 1 }, tableTypes.reverse),
          loadCharts(filters),
          loadUserBalance()
        ]);

        handleLoadedTables(tracking ?? [], tableTypes.tracking);
        handleLoadedTables(reverse ?? [], tableTypes.reverse);
      } catch (e) {
        if (e.length) {
          Swal.fire({
            icon: "error",
            title: "Dashboard",
            text: `Ocorreu um erro ao carregar:\n ${e.join(", ")}`
          });
          return;
        }
        console.log({ e })
        Swal.fire({
          icon: "error",
          title: "Dashboard",
          text: `Ocorreu um erro ao carregar o dashboard`
        });
      } finally {
        setIsTrackingTableLoading(false);
        setIsReverseTableLoading(false);
        setIsLoading(false);
      }
    };

    if (filters) {
      resetData();
    }
  }, [filters]);

  return (
    <section className={styles.main}>
      <Loading loading={isLoading} />

      <span>
        {format(new Date(), "EEEE, dd 'de' MMMM 'de' yyyy", {
          locale: ptBR
        })}
      </span>
      <h1>Bem vindo(a){user?.name ? `, ${user.name}` : "."}</h1>
      <div className={styles.walletBalance}>
        {userBalance > 0 ? (
          <StyledTooltip
            text="Seu crédito pré-aprovado junto à BRCom para contratar fretes de forma faturada. Modalidade voltada para clientes
            com grande volume e recorrência de uso"
            position="bottom"
            arrow
          >
            <span style={{ cursor: "default" }}>
              Seu crédito faturado:
              <b>
                {userBalance !== null
                  ? `${userBalance < 0
                    ? `-${Intl.NumberFormat("pt-BR", {
                      style: "currency",
                      currency: "BRL"
                    }).format(Number(userBalance * -1))}`
                    : `${Intl.NumberFormat("pt-BR", {
                      style: "currency",
                      currency: "BRL"
                    }).format(Number(userBalance))}`
                  }`
                  : "R$ 0.00"}
              </b>
            </span>
          </StyledTooltip>
        ) : (
          <StyledTooltip
            text="Depositado para contratar fretes de forma avulsa. Voltado para clientes que ainda estão escalando seu volume,
            ou fazem envios esporádicos."
            position="bottom"
            arrow
          >
            <span style={{ cursor: "default" }}>
              Seu saldo:
              <b>
                {userWalletBalance !== null
                  ? `${userWalletBalance < 0
                    ? `-${Intl.NumberFormat("pt-BR", {
                      style: "currency",
                      currency: "BRL"
                    }).format(Number(userWalletBalance * -1))}`
                    : `${Intl.NumberFormat("pt-BR", {
                      style: "currency",
                      currency: "BRL"
                    }).format(Number(userWalletBalance))}`
                  }`
                  : "R$ 0.00"}
              </b>
            </span>
          </StyledTooltip>
        )}

        <Link to={"extrato-conta"}>Ver extrato</Link>
      </div>
      <Container fluid className="p-0">
        {chartBySLA ? (
          <ChartBySla
            data={chartBySLA}
            initialDateToRedirect={filters.initialDate}
            endDateToRedirect={filters.endDate}
            customClassName={styles.chartBySLA}
          />
        ) : (
          <Row className={`${styles.cardsStatus}`}>
            <Col xs={12} className={styles.anErrorOcurred}>
              {isLoading ? (
                <Spinner animation="border" role="status">
                  <span style={{ visibility: "hidden" }}>Loading...</span>
                </Spinner>
              ) : (
                <p>Ocorreu um erro ao carregar indicadores.</p>
              )}
            </Col>
          </Row>
        )}
      </Container>

      <Container as="section" fluid className={styles.indicatorsCharts}>
        <ClientFilters onFilter={filters => setFilters(filters)} />

        <Row>
          <Col className={styles.tableStatus}>
            {!!statusTable.length && statusTable.length > 1 ? (
              <TableStatusShippings
                statusTable={statusTable}
                initialDateToRedirect={filters.initialDate}
                endDateToRedirect={filters.endDate}
              />
            ) : (
              <div className={styles.anErrorOcurred}>
                {isLoading ? (
                  <Spinner animation="border" role="status">
                    <span style={{ visibility: "hidden" }}>Loading...</span>
                  </Spinner>
                ) : (
                  <p>Não foram encontrado dados para estes filtro.</p>
                )}
              </div>
            )}
          </Col>

          <Col className={styles.mapWrapper}>
            {chartLocations?.length ? (
              <GoogleMapsDashboard zoom={4} locations={chartLocations} />
            ) : (
              <div className={styles.anErrorOcurred}>
                {isLoading ? <Spinner animation="border" role="status">
                  <span style={{ visibility: "hidden" }}>Loading...</span>
                </Spinner> : <p>Não foi possível carregar o mapa.</p>}
              </div>
            )}
          </Col>

          <Col xs={12} className={styles.tableTracking}>
            <h2 className={styles.title}>
              Rastreamento
              {filters && !!tableTrackingExport.length && (
                <CsvDownload
                  data={tableTrackingExport}
                  filename={`planilha_rastreamento_${format(
                    new Date(filters.initialDate),
                    "dd/MM/yyyy"
                  )}-${format(new Date(filters.endDate), "dd/MM/yyyy")}.csv`}
                >
                  <TbFileExport size={25} color="#409E6A" />{" "}
                  <span>Exportar</span>
                </CsvDownload>
              )}
            </h2>
            <TableCustomerShipping
              trackerTable
              shippings={tableTrackingIndicator}
              handleFetchData={loadMoreTrackingItems}
              isLoading={isTrackingTableLoading}
              hasMore={hasMoreTracking}
            />
          </Col>

          <Col xs={12} className={styles.tableReverse}>
            <h2 className={styles.title}>
              Logística Reversa
              {filters && !!tableReverseExport.length && (
                <CsvDownload
                  data={tableReverseExport}
                  filename={`planilha_logistica_reversa_${format(
                    new Date(filters.initialDate),
                    "dd/MM/yyyy"
                  )}-${format(new Date(filters.endDate), "dd/MM/yyyy")}.csv`}
                >
                  <TbFileExport size={25} color="#409E6A" />{" "}
                  <span>Exportar</span>
                </CsvDownload>
              )}
            </h2>

            <TableCustomerShipping
              shippings={tableReverseLogisticIndicator}
              isLogisticReverse
              handleFetchData={loadMoreReverseItems}
              isLoading={isReverseTableLoading}
              hasMore={hasMoreReverse}
            />
          </Col>
        </Row>
      </Container>
    </section>
  );
};

export { DashboardClient };
